import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, MessageCircle } from "lucide-react";
import { api, formatPrice, imgUrl } from "../lib/api";
import { useCart } from "../context/CartContext";
import ReviewsSection from "../components/ReviewsSection";

const categoryLabel = {
  bois: "Bois",
  verre: "Verre",
  ardoise: "Ardoise",
  flocage: "Flocage",
};

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [active, setActive] = useState(0);
  const [qty, setQty] = useState(1);
  const [notFound, setNotFound] = useState(false);
  const [contact, setContact] = useState({ whatsapp: "" });

  useEffect(() => {
    setNotFound(false);
    setActive(0);
    api
      .get(`/products/${id}`)
      .then((r) => {
        setProduct(r.data);
        // Load related (same category)
        api.get(`/products?category=${r.data.category}`).then((rr) => {
          setRelated(rr.data.filter((p) => p.id !== r.data.id).slice(0, 3));
        });
      })
      .catch(() => setNotFound(true));
    api.get("/contact").then((r) => setContact(r.data)).catch(() => {});
  }, [id]);

  if (notFound) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-24 text-center">
        <h1 className="font-serif text-3xl text-stone-900">Produit introuvable</h1>
        <button
          onClick={() => navigate("/")}
          className="mt-6 px-5 py-2.5 bg-orange-800 hover:bg-orange-900 text-white rounded-sm"
        >
          Retour à la boutique
        </button>
      </div>
    );
  }

  if (!product) {
    return <div className="max-w-7xl mx-auto px-6 py-24 text-center text-stone-400">Chargement…</div>;
  }

  const gallery = [product.image_url, ...(product.images || [])].filter(Boolean);
  const currentImg = gallery[active] || product.image_url;

  return (
    <main className="bg-stone-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8">
        <Link
          to="/"
          data-testid="back-to-shop"
          className="inline-flex items-center gap-2 text-sm text-stone-600 hover:text-orange-800 transition-colors uppercase tracking-[0.15em]"
        >
          <ArrowLeft size={14} /> Retour
        </Link>
      </div>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-16 grid grid-cols-1 lg:grid-cols-2 gap-12">
        {/* Gallery */}
        <div>
          <div className="aspect-square bg-stone-100 border border-stone-200 rounded-sm overflow-hidden">
            <img
              data-testid="detail-main-image"
              src={imgUrl(currentImg)}
              alt={product.name}
              className="w-full h-full object-cover"
            />
          </div>
          {gallery.length > 1 && (
            <div data-testid="detail-thumbnails" className="mt-4 grid grid-cols-5 gap-2">
              {gallery.map((g, i) => (
                <button
                  key={i}
                  data-testid={`thumb-${i}`}
                  onClick={() => setActive(i)}
                  className={`aspect-square border rounded-sm overflow-hidden bg-stone-100 transition-all ${
                    active === i ? "border-orange-700 ring-2 ring-orange-300" : "border-stone-200 hover:border-stone-400"
                  }`}
                >
                  <img src={imgUrl(g)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div>
          <span className="uppercase tracking-[0.18em] text-xs font-semibold text-stone-500">
            {categoryLabel[product.category] || product.category}
          </span>
          <h1 className="mt-2 font-serif text-4xl sm:text-5xl text-stone-900 leading-tight">
            {product.name}
          </h1>
          <div className="mt-6 flex items-baseline gap-3">
            <span
              data-testid="detail-price"
              className="text-4xl font-black text-orange-700 tracking-tight"
            >
              {formatPrice(product.price)}
            </span>
            {product.in_stock ? (
              <span className="text-xs uppercase tracking-[0.15em] px-2 py-0.5 bg-green-100 text-green-800 rounded-sm">En stock</span>
            ) : (
              <span className="text-xs uppercase tracking-[0.15em] px-2 py-0.5 bg-stone-200 text-stone-700 rounded-sm">Rupture</span>
            )}
          </div>

          {product.description && (
            <p className="mt-6 text-stone-700 leading-relaxed whitespace-pre-line">
              {product.description}
            </p>
          )}

          <div className="mt-8 flex items-center gap-3">
            <div className="flex items-center border border-stone-300 rounded-sm">
              <button
                data-testid="detail-qty-minus"
                onClick={() => setQty(Math.max(1, qty - 1))}
                className="px-3 py-2 hover:bg-stone-100"
              >
                −
              </button>
              <span data-testid="detail-qty" className="px-4 font-medium">{qty}</span>
              <button
                data-testid="detail-qty-plus"
                onClick={() => setQty(qty + 1)}
                className="px-3 py-2 hover:bg-stone-100"
              >
                +
              </button>
            </div>
            <button
              data-testid="detail-add-to-cart"
              onClick={() => addItem({ ...product, image_url: imgUrl(product.image_url) }, qty)}
              disabled={!product.in_stock}
              className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 bg-orange-800 hover:bg-orange-900 disabled:bg-stone-300 text-white font-medium rounded-sm transition-colors"
            >
              <Plus size={18} /> Ajouter au panier
            </button>
          </div>

          {contact.whatsapp && (
            <a
              data-testid="detail-whatsapp"
              href={`https://wa.me/${contact.whatsapp}?text=${encodeURIComponent(
                `Bonjour Bidoudou50.fr, j'aimerais en savoir plus sur "${product.name}" (${formatPrice(product.price)}).`
              )}`}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 w-full inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-[#25D366] hover:bg-[#1ebd57] text-white font-medium rounded-sm transition-colors"
            >
              <MessageCircle size={18} /> Demander un devis personnalisé
            </a>
          )}

          <div className="mt-10 border-t border-stone-200 pt-6 grid grid-cols-2 gap-4 text-sm text-stone-600">
            <div>
              <div className="uppercase tracking-[0.15em] text-xs font-semibold text-stone-400 mb-1">Fabrication</div>
              <div>Atelier artisanal Bidoudou50.fr</div>
            </div>
            <div>
              <div className="uppercase tracking-[0.15em] text-xs font-semibold text-stone-400 mb-1">Personnalisable</div>
              <div>Oui — précisez votre demande au panier</div>
            </div>
          </div>
        </div>
      </section>

      {related.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-16">
          <h2 className="font-serif text-2xl sm:text-3xl text-stone-900 mb-6">Vous aimerez aussi</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {related.map((p) => (
              <Link
                to={`/produit/${p.id}`}
                key={p.id}
                data-testid={`related-${p.id}`}
                className="bg-white border border-stone-200 rounded-sm overflow-hidden group"
              >
                <div className="product-image-wrap aspect-[4/3] bg-stone-100">
                  <img src={imgUrl(p.image_url)} alt={p.name} className="w-full h-full object-cover" />
                </div>
                <div className="p-4">
                  <h3 className="font-serif text-lg text-stone-900">{p.name}</h3>
                  <p className="mt-1 text-orange-700 font-black">{formatPrice(p.price)}</p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      <ReviewsSection productId={product.id} compact />
    </main>
  );
}
